// src/screens/ChartsScreen.tsx - カレンダー組み込み完全版
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Dialog,
  Divider,
  Tabs,
  Tab
} from '@mui/material';
import {
  ShowChart,
  Analytics,
  BarChart as BarChartIcon,
  Timeline,
  PieChart as PieChartIcon,
  CalendarToday,
  DateRange,
  ChevronLeft,
  ChevronRight
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartsPieChart, Pie, Cell, Legend, PieLabelRenderProps, BarChart, Bar } from 'recharts';
import { getAllDeliveries, getAllExpenses } from '../services/dataService';
import { DeliveryRecord, Expense, calculateActualWorkingMinutes } from '../types';

interface Props {
  onShowSnackbar: (message: string, severity?: 'success' | 'error') => void;
  simpleMode?: boolean;
}

type DateRangeValue = {
  start: string;
  end: string;
};

function ChartsScreen({ onShowSnackbar, simpleMode = false }: Props) {
  const [deliveries, setDeliveries] = useState<DeliveryRecord[]>([]);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [selectedPeriod, setSelectedPeriod] = useState('week');
  const [selectedSpecificDate, setSelectedSpecificDate] = useState<Date | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
  const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showDateRangePicker, setShowDateRangePicker] = useState(false);
  const [activeChartTab, setActiveChartTab] = useState('overview');
  const [loading, setLoading] = useState(true);

  // カレンダー用の状態
  const [calendarDate, setCalendarDate] = useState(new Date());
  const [calendarYM, setCalendarYM] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth()
  });
  const [rangeStart, setRangeStart] = useState<Date | null>(null);
  const [rangeEnd, setRangeEnd] = useState<Date | null>(null);
  const [selectingStart, setSelectingStart] = useState(true);
  const [rangeCalendarYM, setRangeCalendarYM] = useState({
    year: new Date().getFullYear(),
    month: new Date().getMonth()
  });

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (simpleMode && (activeChartTab === 'hourly' || activeChartTab === 'service')) {
      setActiveChartTab('overview');
    }
  }, [activeChartTab, simpleMode]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [deliveriesData, expensesData] = await Promise.all([
        getAllDeliveries(),
        getAllExpenses()
      ]);
      setDeliveries(deliveriesData);
      setExpenses(expensesData);
    } catch (error) {
      onShowSnackbar('データの読み込みに失敗しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const dateToString = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const selectedRange = useMemo<DateRangeValue | null>(() => {
    const today = new Date();
    
    switch (selectedPeriod) {
      case 'today':
        return { start: dateToString(today), end: dateToString(today) };
      case 'week': {
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() - today.getDay() + (today.getDay() === 0 ? -6 : 1));
        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        return { start: dateToString(startOfWeek), end: dateToString(endOfWeek) };
      }
      case 'month': {
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        return { start: dateToString(startOfMonth), end: dateToString(endOfMonth) };
      }
      case 'specific':
        if (!selectedSpecificDate) return null;
        return { start: dateToString(selectedSpecificDate), end: dateToString(selectedSpecificDate) };
      case 'range':
        if (!selectedStartDate || !selectedEndDate) return null;
        return {
          start: dateToString(selectedStartDate < selectedEndDate ? selectedStartDate : selectedEndDate),
          end: dateToString(selectedStartDate < selectedEndDate ? selectedEndDate : selectedStartDate)
        };
      default:
        return null;
    }
  }, [selectedPeriod, selectedSpecificDate, selectedStartDate, selectedEndDate]);

  const filteredDeliveries = useMemo(() => {
    if (selectedPeriod !== 'all' && selectedRange) {
      return deliveries.filter(d => d.date >= selectedRange.start && d.date <= selectedRange.end);
    }
    return deliveries;
  }, [deliveries, selectedPeriod, selectedRange]);

  const filteredExpenses = useMemo(() => {
    if (selectedPeriod !== 'all' && selectedRange) {
      return expenses.filter(e => e.date >= selectedRange.start && e.date <= selectedRange.end);
    }
    return expenses;
  }, [expenses, selectedPeriod, selectedRange]);

  const totalRevenue = filteredDeliveries.reduce((sum, d) => sum + d.totalRevenue, 0);
  const totalExpense = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);
  const netProfit = totalRevenue - totalExpense;
  const totalCount = filteredDeliveries.reduce((sum, d) => sum + d.deliveryCount, 0);

  // 日付ごとにグループ化して実働時間を計算
  const dateGroups = useMemo(() => {
    const groups = new Map<string, DeliveryRecord[]>();
    filteredDeliveries.forEach(d => {
      const group = groups.get(d.date) || [];
      group.push(d);
      groups.set(d.date, group);
    });
    return groups;
  }, [filteredDeliveries]);

  // 各日の実働時間を計算して合計
  const totalMinutes = useMemo(() => {
    return Array.from(dateGroups.values())
      .reduce((sum, dayDeliveries) => sum + calculateActualWorkingMinutes(dayDeliveries), 0);
  }, [dateGroups]);

  const avgHourlyRate = totalMinutes > 0 ? Math.floor((totalRevenue / totalMinutes) * 60) : 0;
  const avgProfitHourlyRate = totalMinutes > 0 ? Math.floor((netProfit / totalMinutes) * 60) : 0;
  const avgPerDelivery = totalCount > 0 ? Math.floor(totalRevenue / totalCount) : 0;
  const profitRate = totalRevenue > 0 ? Math.round((netProfit / totalRevenue) * 100) : 0;

  const formatCurrency = (amount: number) => `¥${amount.toLocaleString()}`;
  const formatMinutes = (minutes: number) => `${Math.floor(minutes / 60)}h${minutes % 60}m`;
  const formatPercent = (value: number) => `${value > 0 ? '+' : ''}${value}%`;

  const dateFromString = (value: string) => new Date(`${value}T00:00:00`);
  const addDays = (date: Date, days: number) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    return next;
  };

  const previousRange = useMemo<DateRangeValue | null>(() => {
    if (!selectedRange || selectedPeriod === 'all') return null;
    const start = dateFromString(selectedRange.start);
    const end = dateFromString(selectedRange.end);
    const days = Math.max(1, Math.round((end.getTime() - start.getTime()) / 86400000) + 1);
    const prevEnd = addDays(start, -1);
    const prevStart = addDays(prevEnd, -(days - 1));
    return {
      start: dateToString(prevStart),
      end: dateToString(prevEnd)
    };
  }, [selectedRange, selectedPeriod]);

  const previousDeliveries = useMemo(() => {
    if (!previousRange) return [];
    return deliveries.filter(d => d.date >= previousRange.start && d.date <= previousRange.end);
  }, [deliveries, previousRange]);

  const previousExpenses = useMemo(() => {
    if (!previousRange) return [];
    return expenses.filter(e => e.date >= previousRange.start && e.date <= previousRange.end);
  }, [expenses, previousRange]);

  const previousSummary = useMemo(() => {
    if (!previousRange) return null;
    const revenue = previousDeliveries.reduce((sum, d) => sum + d.totalRevenue, 0);
    const expense = previousExpenses.reduce((sum, e) => sum + e.amount, 0);
    const groups = new Map<string, DeliveryRecord[]>();
    previousDeliveries.forEach(d => {
      const group = groups.get(d.date) || [];
      group.push(d);
      groups.set(d.date, group);
    });
    const minutes = Array.from(groups.values())
      .reduce((sum, dayDeliveries) => sum + calculateActualWorkingMinutes(dayDeliveries), 0);
    const count = previousDeliveries.reduce((sum, d) => sum + d.deliveryCount, 0);
    return {
      revenue,
      expense,
      profit: revenue - expense,
      minutes,
      count,
      hourlyRate: minutes > 0 ? Math.floor((revenue / minutes) * 60) : 0
    };
  }, [previousRange, previousDeliveries, previousExpenses]);

  const getDeltaPercent = (current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  const getFriendlyDelta = (current: number, previous: number, label: string) => {
    if (previous === 0 && current === 0) return `${label}は前と同じくらいです`;
    if (previous === 0) return `${label}は前より増えています`;

    const delta = getDeltaPercent(current, previous);
    const absDelta = Math.abs(delta);
    if (absDelta < 5) return `${label}は前とほぼ同じです`;

    const direction = delta > 0 ? '多め' : '少なめ';
    const degree = absDelta >= 50 ? 'かなり' : absDelta >= 20 ? 'はっきり' : '少し';
    return `${label}は前より${degree}${direction}です`;
  };

  const getDeltaShortLabel = (value: number) => {
    const absValue = Math.abs(value);
    if (absValue < 5) return 'ほぼ同じ';
    const direction = value > 0 ? '多め' : '少なめ';
    const degree = absValue >= 50 ? 'かなり' : absValue >= 20 ? 'はっきり' : '少し';
    return `${degree}${direction}`;
  };

  const periodLabel = useMemo(() => {
    if (selectedPeriod === 'today') return '今日';
    if (selectedPeriod === 'week') return '今週';
    if (selectedPeriod === 'month') return '今月';
    if (selectedPeriod === 'specific' && selectedSpecificDate) {
      return selectedSpecificDate.toLocaleDateString('ja-JP', { month: 'long', day: 'numeric' });
    }
    if (selectedPeriod === 'range' && selectedStartDate && selectedEndDate) {
      return `${selectedStartDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })} - ${selectedEndDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}`;
    }
    return '全期間';
  }, [selectedPeriod, selectedSpecificDate, selectedStartDate, selectedEndDate]);

  const COLORS = ['#10B981', '#3B82F6', '#8B5CF6', '#F59E0B', '#EF4444'];

  const getDailyRevenueData = () => {
    const dailyMap = new Map<string, number>();
    
    filteredDeliveries.forEach(d => {
      const curr = dailyMap.get(d.date) || 0;
      dailyMap.set(d.date, curr + d.totalRevenue);
    });

    const entries = Array.from(dailyMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const last30 = entries.slice(-30);
    
    return last30.map(([date, revenue]) => ({
      date: new Date(date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
      revenue
    }));
  };

  const getHourlyRateData = () => {
    // 日付ごとにグループ化
    const rateMap = new Map<string, { revenue: number; minutes: number }>();
    
    dateGroups.forEach((dayDeliveries, date) => {
      const revenue = dayDeliveries.reduce((sum, d) => sum + d.totalRevenue, 0);
      const minutes = calculateActualWorkingMinutes(dayDeliveries);
      rateMap.set(date, { revenue, minutes });
    });

    const entries = Array.from(rateMap.entries()).sort((a, b) => a[0].localeCompare(b[0]));
    const last30 = entries.slice(-30);
    
    return last30.map(([date, data]) => ({
      date: new Date(date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
      rate: data.minutes > 0 ? Math.floor((data.revenue / data.minutes) * 60) : 0
    }));
  };

  const serviceStats = useMemo(() => {
    const platformMap = new Map<string, { name: string; revenue: number; count: number; minutes: number }>();
    
    filteredDeliveries.forEach(d => {
      const platform = d.platform || '未設定';
      const current = platformMap.get(platform) || { name: platform, revenue: 0, count: 0, minutes: 0 };
      platformMap.set(platform, {
        name: platform,
        revenue: current.revenue + d.totalRevenue,
        count: current.count + d.deliveryCount,
        minutes: current.minutes + calculateActualWorkingMinutes([d])
      });
    });

    return Array.from(platformMap.values())
      .map(stat => ({
        ...stat,
        hourlyRate: stat.minutes > 0 ? Math.floor((stat.revenue / stat.minutes) * 60) : 0,
        avgRevenue: stat.count > 0 ? Math.floor(stat.revenue / stat.count) : 0,
        share: totalRevenue > 0 ? Math.round((stat.revenue / totalRevenue) * 100) : 0
      }))
      .sort((a, b) => b.revenue - a.revenue);
  }, [filteredDeliveries, totalRevenue]);

  const expenseCategoryStats = useMemo(() => {
    const categoryMap = new Map<string, { name: string; amount: number; count: number }>();

    filteredExpenses.forEach(e => {
      const category = e.category || '未設定';
      const current = categoryMap.get(category) || { name: category, amount: 0, count: 0 };
      categoryMap.set(category, {
        name: category,
        amount: current.amount + e.amount,
        count: current.count + 1
      });
    });

    return Array.from(categoryMap.values())
      .map(stat => ({
        ...stat,
        share: totalExpense > 0 ? Math.round((stat.amount / totalExpense) * 100) : 0
      }))
      .sort((a, b) => b.amount - a.amount);
  }, [filteredExpenses, totalExpense]);

  const getPlatformData = () => {
    return serviceStats
      .map(stat => ({ name: stat.name, value: stat.revenue }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5);
  };

  const dailyRevenueData = getDailyRevenueData();
  const hourlyRateData = getHourlyRateData();
  const platformData = getPlatformData();
  const topRevenueService = serviceStats[0];
  const topHourlyService = [...serviceStats].sort((a, b) => b.hourlyRate - a.hourlyRate)[0];
  const topExpenseCategory = expenseCategoryStats[0];

  const insightMessages = useMemo(() => {
    const messages: string[] = [];
    if (filteredDeliveries.length === 0) {
      messages.push('この期間の配達記録はまだありません。期間を変えるか、記録を追加すると分析できます。');
      return messages;
    }

    if (!simpleMode && topHourlyService && topHourlyService.hourlyRate > 0) {
      messages.push(`${topHourlyService.name}の時給が最も高く、${formatCurrency(topHourlyService.hourlyRate)}/h です。`);
    }
    if (topRevenueService) {
      messages.push(`売上は${topRevenueService.name}が最大で、全体の${topRevenueService.share}%を占めています。`);
    }
    if (previousSummary) {
      messages.push(`${getFriendlyDelta(totalRevenue, previousSummary.revenue, '売上')}。${getFriendlyDelta(netProfit, previousSummary.profit, '利益')}。`);
    }
    if (topExpenseCategory && totalExpense > 0) {
      messages.push(`支出は${topExpenseCategory.name}が一番多めです。全体の${topExpenseCategory.share}%を占めています。`);
    }
    if (totalRevenue > 0 && profitRate < 70) {
      messages.push(`利益率は${profitRate}%です。経費カテゴリを見直すと手残り改善につながります。`);
    }

    return messages.slice(0, 3);
  }, [filteredDeliveries.length, topHourlyService, topRevenueService, previousSummary, topExpenseCategory, totalRevenue, totalExpense, netProfit, profitRate]);

  const dailyFinancialData = useMemo(() => {
    const dailyMap = new Map<string, { revenue: number; expense: number; count: number }>();

    filteredDeliveries.forEach(d => {
      const current = dailyMap.get(d.date) || { revenue: 0, expense: 0, count: 0 };
      dailyMap.set(d.date, {
        ...current,
        revenue: current.revenue + d.totalRevenue,
        count: current.count + d.deliveryCount
      });
    });

    filteredExpenses.forEach(e => {
      const current = dailyMap.get(e.date) || { revenue: 0, expense: 0, count: 0 };
      dailyMap.set(e.date, {
        ...current,
        expense: current.expense + e.amount
      });
    });

    return Array.from(dailyMap.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .slice(-30)
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('ja-JP', { month: 'short', day: 'numeric' }),
        revenue: data.revenue,
        expense: data.expense,
        profit: data.revenue - data.expense,
        count: data.count
      }));
  }, [filteredDeliveries, filteredExpenses]);

  // カレンダー用の計算
  const daysInMonth = useMemo(
    () => new Date(calendarYM.year, calendarYM.month + 1, 0).getDate(),
    [calendarYM]
  );
  const firstDayOfWeek = useMemo(
    () => new Date(calendarYM.year, calendarYM.month, 1).getDay(),
    [calendarYM]
  );

  const rangeDaysInMonth = useMemo(
    () => new Date(rangeCalendarYM.year, rangeCalendarYM.month + 1, 0).getDate(),
    [rangeCalendarYM]
  );
  const rangeFirstDayOfWeek = useMemo(
    () => new Date(rangeCalendarYM.year, rangeCalendarYM.month, 1).getDay(),
    [rangeCalendarYM]
  );

  const prevMonth = () => {
    if (calendarYM.month === 0) {
      setCalendarYM({ year: calendarYM.year - 1, month: 11 });
    } else {
      setCalendarYM({ year: calendarYM.year, month: calendarYM.month - 1 });
    }
  };

  const nextMonth = () => {
    if (calendarYM.month === 11) {
      setCalendarYM({ year: calendarYM.year + 1, month: 0 });
    } else {
      setCalendarYM({ year: calendarYM.year, month: calendarYM.month + 1 });
    }
  };

  const rangePrevMonth = () => {
    if (rangeCalendarYM.month === 0) {
      setRangeCalendarYM({ year: rangeCalendarYM.year - 1, month: 11 });
    } else {
      setRangeCalendarYM({ year: rangeCalendarYM.year, month: rangeCalendarYM.month - 1 });
    }
  };

  const rangeNextMonth = () => {
    if (rangeCalendarYM.month === 11) {
      setRangeCalendarYM({ year: rangeCalendarYM.year + 1, month: 0 });
    } else {
      setRangeCalendarYM({ year: rangeCalendarYM.year, month: rangeCalendarYM.month + 1 });
    }
  };

  const isSameDay = (d1: Date, d2: Date) => {
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  const isInRange = (date: Date) => {
    if (!rangeStart || !rangeEnd) return false;
    const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
    const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
    return date >= start && date <= end;
  };

  if (loading) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="white">読み込み中...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#0b0f0d', minHeight: '100vh', pb: 10 }}>
      <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
        <Typography fontSize={15} color="#8a948d" fontWeight="medium">
          統計
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 2 }}>
          <Typography fontSize={24} fontWeight={800} color="white">
            売上分析
          </Typography>
          <Chip
            label={periodLabel}
            size="small"
            sx={{ bgcolor: 'rgba(59, 130, 246, 0.14)', color: '#93C5FD', fontWeight: 700 }}
          />
        </Box>
      </Box>

      {/* 期間選択 */}
      <Card sx={{ m: 2, bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
            <ShowChart sx={{ fontSize: 20, color: '#10B981', mr: 1 }} />
            <Typography fontSize={17} fontWeight="bold" color="white">
              期間選択
            </Typography>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 1 }}>
            {['today', 'week', 'month'].map((period) => (
              <Chip
                key={period}
                label={period === 'today' ? '今日' : period === 'week' ? '今週' : '今月'}
                onClick={() => setSelectedPeriod(period)}
                sx={{
                  bgcolor: selectedPeriod === period ? '#10B981' : '#2a2a2a',
                  color: selectedPeriod === period ? '#000' : '#888',
                  fontWeight: selectedPeriod === period ? 'bold' : 'normal',
                  '&:hover': {
                    bgcolor: selectedPeriod === period ? '#0d9668' : '#333'
                  }
                }}
              />
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
            <Chip
              label="全期間"
              onClick={() => setSelectedPeriod('all')}
              sx={{
                bgcolor: selectedPeriod === 'all' ? '#10B981' : '#2a2a2a',
                color: selectedPeriod === 'all' ? '#000' : '#888',
                fontWeight: selectedPeriod === 'all' ? 'bold' : 'normal',
                '&:hover': {
                  bgcolor: selectedPeriod === 'all' ? '#0d9668' : '#333'
                }
              }}
            />

            <Button
              fullWidth
              variant="outlined"
              startIcon={<CalendarToday sx={{ fontSize: 16 }} />}
              onClick={() => setShowDatePicker(true)}
              sx={{
                borderColor: selectedPeriod === 'specific' ? '#10B981' : '#333',
                color: selectedPeriod === 'specific' ? '#10B981' : '#888',
                fontSize: 13,
                py: 1,
                '&:hover': {
                  borderColor: selectedPeriod === 'specific' ? '#10B981' : '#444',
                  bgcolor: 'transparent'
                }
              }}
            >
              {selectedPeriod === 'specific' && selectedSpecificDate
                ? selectedSpecificDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })
                : '特定日'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              startIcon={<DateRange sx={{ fontSize: 16 }} />}
              onClick={() => setShowDateRangePicker(true)}
              sx={{
                borderColor: selectedPeriod === 'range' ? '#10B981' : '#333',
                color: selectedPeriod === 'range' ? '#10B981' : '#888',
                fontSize: 13,
                py: 1,
                '&:hover': {
                  borderColor: selectedPeriod === 'range' ? '#10B981' : '#444',
                  bgcolor: 'transparent'
                }
              }}
            >
              {selectedPeriod === 'range' && selectedStartDate && selectedEndDate
                ? `${selectedStartDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}-${selectedEndDate.toLocaleDateString('ja-JP', { month: 'numeric', day: 'numeric' })}`
                : '期間指定'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Box sx={{ px: 2, pt: 0 }}>
        {/* 期間サマリー */}
        <Card sx={{ bgcolor: '#102118', border: '1px solid #214332', borderRadius: '12px', mb: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Analytics sx={{ fontSize: 20, color: '#34D399', mr: 1 }} />
                <Typography fontSize={17} fontWeight="bold" color="white">
                  期間サマリー
                </Typography>
              </Box>
              <Typography fontSize={14} color="#9ca7a0">
                売上 - 経費
              </Typography>
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mb: 1.25 }}>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.25, textAlign: 'center' }}>
                <Typography fontSize={14} color="#888">純利益</Typography>
                <Typography fontSize={17} fontWeight="bold" color={netProfit >= 0 ? '#10B981' : '#EF4444'}>
                  {formatCurrency(netProfit)}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.25, textAlign: 'center' }}>
                <Typography fontSize={14} color="#888">売上</Typography>
                <Typography fontSize={17} fontWeight="bold" color="white">
                  {formatCurrency(totalRevenue)}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, p: 1.25, textAlign: 'center' }}>
                <Typography fontSize={14} color="#888">経費</Typography>
                <Typography fontSize={17} fontWeight="bold" color="#EF4444">
                  {formatCurrency(totalExpense)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 1.5, bgcolor: '#2a4a3a' }} />

            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
              {!simpleMode && (
                <>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography fontSize={14} color="#888">平均時給</Typography>
                    <Typography fontSize={17} fontWeight="bold" color="#8B5CF6">
                      {formatCurrency(avgHourlyRate)}
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography fontSize={14} color="#888">稼働時間</Typography>
                    <Typography fontSize={17} fontWeight="bold" color="#3B82F6">
                      {Math.floor(totalMinutes / 60)}h{totalMinutes % 60}m
                    </Typography>
                  </Box>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography fontSize={14} color="#888">利益時給</Typography>
                    <Typography fontSize={17} fontWeight="bold" color={avgProfitHourlyRate >= 0 ? '#34D399' : '#EF4444'}>
                      {formatCurrency(avgProfitHourlyRate)}
                    </Typography>
                  </Box>
                </>
              )}
              <Box sx={{ textAlign: 'center' }}>
                <Typography fontSize={14} color="#888">件数単価</Typography>
                <Typography fontSize={17} fontWeight="bold" color="#F59E0B">
                  {formatCurrency(avgPerDelivery)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
          <CardContent sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                <Analytics sx={{ fontSize: 20, color: '#93C5FD', mr: 1 }} />
                <Typography fontSize={17} fontWeight="bold" color="white">
                  ひとこと分析
                </Typography>
              </Box>
              {previousSummary && (
                <Chip
                  label="前期間比"
                  size="small"
                  sx={{ bgcolor: 'rgba(59,130,246,0.14)', color: '#93C5FD', fontWeight: 700 }}
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {insightMessages.map((message, index) => (
                <Box
                  key={index}
                  sx={{
                    bgcolor: index === 0 ? 'rgba(16,185,129,0.10)' : 'rgba(255,255,255,0.045)',
                    border: index === 0 ? '1px solid rgba(16,185,129,0.22)' : '1px solid rgba(255,255,255,0.06)',
                    borderRadius: '10px',
                    p: 1.25
                  }}
                >
                  <Typography fontSize={15} color={index === 0 ? '#D1FAE5' : '#d5ddd8'} lineHeight={1.55}>
                    {message}
                  </Typography>
                </Box>
              ))}
            </Box>

            {previousSummary && (
              <>
                <Divider sx={{ my: 1.5, bgcolor: '#26312b' }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1 }}>
                  {[
                    { label: '売上', value: getDeltaPercent(totalRevenue, previousSummary.revenue), color: '#10B981' },
                    { label: '利益', value: getDeltaPercent(netProfit, previousSummary.profit), color: '#F59E0B' },
                    ...(simpleMode ? [] : [{ label: '時給', value: getDeltaPercent(avgHourlyRate, previousSummary.hourlyRate), color: '#8B5CF6' }])
                  ].map((item) => (
                    <Box key={item.label} sx={{ bgcolor: 'rgba(255,255,255,0.045)', borderRadius: '8px', p: 1, textAlign: 'center' }}>
                      <Typography fontSize={13} color="#8a948d">{item.label}</Typography>
                      <Typography fontSize={15} fontWeight={800} color={item.value >= 0 ? item.color : '#EF4444'} noWrap>
                        {getDeltaShortLabel(item.value)}
                      </Typography>
                      <Typography fontSize={11} color="#6f7b74">
                        {formatPercent(item.value)}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </CardContent>
        </Card>

        <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
          <Tabs
            value={activeChartTab}
            onChange={(_, value) => setActiveChartTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              px: 1,
              '& .MuiTab-root': {
                color: '#8a948d',
                fontSize: 13,
                fontWeight: 700,
                minHeight: 44,
                px: 1.5
              },
              '& .Mui-selected': { color: '#34D399' },
              '& .MuiTabs-indicator': { bgcolor: '#34D399' }
            }}
          >
            <Tab label="概要" value="overview" />
            <Tab label="売上" value="revenue" />
            <Tab label="利益" value="profit" />
            <Tab label="支出" value="expense" />
            {!simpleMode && <Tab label="時給" value="hourly" />}
            <Tab label="件数" value="count" />
            {!simpleMode && <Tab label="サービス" value="service" />}
          </Tabs>
          <Box
            sx={{
              px: 1.5,
              pb: 1.25,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1
            }}
          >
            <Typography fontSize={12} color="#8a948d">
              見たい項目を選んで分析できます
            </Typography>
            <Typography
              fontSize={12}
              fontWeight={800}
              color="#FCD34D"
              sx={{
                px: 1,
                py: 0.35,
                borderRadius: '999px',
                bgcolor: 'rgba(245,158,11,0.12)',
                border: '1px solid rgba(245,158,11,0.20)',
                flexShrink: 0
              }}
            >
              ← 横にスライド
            </Typography>
          </Box>
        </Card>

        {activeChartTab === 'overview' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!simpleMode && (
            <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <PieChartIcon sx={{ fontSize: 20, color: '#3B82F6', mr: 1 }} />
                    <Typography fontSize={17} fontWeight="bold" color="white">
                      サービス実力比較
                    </Typography>
                  </Box>
                  <Typography fontSize={14} color="#8a948d">
                    時給・単価・稼働
                  </Typography>
                </Box>

                {serviceStats.length === 0 ? (
                  <Box sx={{ py: 5, textAlign: 'center' }}>
                    <ShowChart sx={{ fontSize: 44, color: '#444', mb: 1 }} />
                    <Typography fontSize={16} color="#666">データがありません</Typography>
                  </Box>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                    {serviceStats.slice(0, 5).map((stat, index) => (
                      <Box
                        key={stat.name}
                        sx={{
                          bgcolor: index === 0 ? 'rgba(59,130,246,0.10)' : 'rgba(255,255,255,0.045)',
                          border: index === 0 ? '1px solid rgba(59,130,246,0.24)' : '1px solid rgba(255,255,255,0.06)',
                          borderRadius: '10px',
                          p: 1.25
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontSize={16} fontWeight={800} color="white" noWrap>
                              {stat.name}
                            </Typography>
                            <Typography fontSize={13} color="#8a948d">
                              {stat.count}件 / {formatMinutes(stat.minutes)}
                            </Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
                            <Typography fontSize={17} fontWeight={800} color="#10B981">
                              {formatCurrency(stat.revenue)}
                            </Typography>
                            <Typography fontSize={13} color="#93C5FD">
                              {stat.share}%
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                          <Box sx={{ bgcolor: 'rgba(139,92,246,0.12)', borderRadius: '8px', p: 1 }}>
                            <Typography fontSize={12} color="#c4b5fd">時給</Typography>
                            <Typography fontSize={15} fontWeight={800} color="#c4b5fd">
                              {formatCurrency(stat.hourlyRate)}
                            </Typography>
                          </Box>
                          <Box sx={{ bgcolor: 'rgba(245,158,11,0.12)', borderRadius: '8px', p: 1 }}>
                            <Typography fontSize={12} color="#FCD34D">件数単価</Typography>
                            <Typography fontSize={15} fontWeight={800} color="#FCD34D">
                              {formatCurrency(stat.avgRevenue)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </CardContent>
            </Card>
            )}

            {dailyFinancialData.length > 0 && (
              <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Timeline sx={{ fontSize: 20, color: '#F59E0B', mr: 1 }} />
                      <Typography fontSize={17} fontWeight="bold" color="white">
                        売上・利益の流れ
                      </Typography>
                    </Box>
                    <Typography fontSize={14} color="#8a948d">
                      最大30日
                    </Typography>
                  </Box>

                  <ResponsiveContainer width="100%" height={240}>
                    <LineChart data={dailyFinancialData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                      <XAxis dataKey="date" stroke="#888" fontSize={10} tick={{ fill: '#888' }} />
                      <YAxis stroke="#888" fontSize={10} tick={{ fill: '#888' }} tickFormatter={(value) => `¥${value}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value: number, name: string) => [`¥${value.toLocaleString()}`, name === 'revenue' ? '売上' : '利益']}
                      />
                      <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981', r: 3 }} />
                      <Line type="monotone" dataKey="profit" stroke="#F59E0B" strokeWidth={3} dot={{ fill: '#F59E0B', r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </Box>
        )}

        {/* 売上推移 */}
        {activeChartTab === 'revenue' && dailyRevenueData.length > 0 && (
          <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon sx={{ fontSize: 20, color: '#10B981', mr: 1 }} />
                  <Typography fontSize={17} fontWeight="bold" color="white">
                    売上推移
                  </Typography>
                </Box>
                <Typography fontSize={14} color="#8a948d">
                  最大30日
                </Typography>
              </Box>

              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={dailyRevenueData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#888" 
                    fontSize={10}
                    tick={{ fill: '#888' }}
                  />
                  <YAxis 
                    stroke="#888" 
                    fontSize={10}
                    tick={{ fill: '#888' }}
                    tickFormatter={(value) => `¥${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: number) => [`¥${value.toLocaleString()}`, '売上']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#10B981" 
                    strokeWidth={2}
                    dot={{ fill: '#10B981', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {activeChartTab === 'profit' && (
          <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon sx={{ fontSize: 20, color: '#F59E0B', mr: 1 }} />
                  <Typography fontSize={17} fontWeight="bold" color="white">
                    利益推移
                  </Typography>
                </Box>
                <Typography fontSize={14} color="#8a948d">
                  売上・支出込み
                </Typography>
              </Box>

              {dailyFinancialData.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                  <ShowChart sx={{ fontSize: 48, color: '#444', mb: 1 }} />
                  <Typography fontSize={16} color="#666">データがありません</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart data={dailyFinancialData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                    <XAxis dataKey="date" stroke="#888" fontSize={10} tick={{ fill: '#888' }} />
                    <YAxis stroke="#888" fontSize={10} tick={{ fill: '#888' }} tickFormatter={(value) => `¥${value}`} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value: number, name: string) => [`¥${value.toLocaleString()}`, name === 'revenue' ? '売上' : name === 'expense' ? '支出' : '利益']}
                    />
                    <Line type="monotone" dataKey="revenue" stroke="#10B981" strokeWidth={2} dot={{ fill: '#10B981', r: 3 }} />
                    <Line type="monotone" dataKey="expense" stroke="#EF4444" strokeWidth={2} dot={{ fill: '#EF4444', r: 3 }} />
                    <Line type="monotone" dataKey="profit" stroke="#F59E0B" strokeWidth={3} dot={{ fill: '#F59E0B', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        )}

        {activeChartTab === 'expense' && (
          <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <BarChartIcon sx={{ fontSize: 20, color: '#EF4444', mr: 1 }} />
                  <Typography fontSize={17} fontWeight="bold" color="white">
                    支出分析
                  </Typography>
                </Box>
                <Typography fontSize={14} color="#8a948d">
                  カテゴリ別
                </Typography>
              </Box>

              {expenseCategoryStats.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                  <ShowChart sx={{ fontSize: 48, color: '#444', mb: 1 }} />
                  <Typography fontSize={16} color="#666">支出データがありません</Typography>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 1, mb: 2 }}>
                    <Box sx={{ bgcolor: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.20)', borderRadius: '10px', p: 1.25, textAlign: 'center' }}>
                      <Typography fontSize={13} color="#FCA5A5">総支出</Typography>
                      <Typography fontSize={17} fontWeight={800} color="#FCA5A5">
                        {formatCurrency(totalExpense)}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.20)', borderRadius: '10px', p: 1.25, textAlign: 'center' }}>
                      <Typography fontSize={13} color="#FCD34D">最大カテゴリ</Typography>
                      <Typography fontSize={15} fontWeight={800} color="#FCD34D" noWrap>
                        {topExpenseCategory?.name || '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.20)', borderRadius: '10px', p: 1.25, textAlign: 'center' }}>
                      <Typography fontSize={13} color="#93C5FD">売上に対して</Typography>
                      <Typography fontSize={17} fontWeight={800} color="#93C5FD">
                        {totalRevenue > 0 ? `${Math.round((totalExpense / totalRevenue) * 100)}%` : '-'}
                      </Typography>
                    </Box>
                  </Box>

                  <ResponsiveContainer width="100%" height={230}>
                    <BarChart data={expenseCategoryStats.slice(0, 6)}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                      <XAxis dataKey="name" stroke="#888" fontSize={10} tick={{ fill: '#888' }} />
                      <YAxis stroke="#888" fontSize={10} tick={{ fill: '#888' }} tickFormatter={(value) => `¥${value}`} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value: number) => [`¥${value.toLocaleString()}`, '支出']}
                      />
                      <Bar dataKey="amount" fill="#EF4444" radius={[6, 6, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>

                  <Divider sx={{ my: 2, bgcolor: '#26312b' }} />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {expenseCategoryStats.slice(0, 6).map((stat, index) => (
                      <Box key={stat.name} sx={{ bgcolor: 'rgba(255,255,255,0.045)', borderRadius: '8px', p: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography fontSize={15} fontWeight={800} color="white" noWrap>
                            {index + 1}. {stat.name}
                          </Typography>
                          <Typography fontSize={15} fontWeight={800} color="#FCA5A5" sx={{ flexShrink: 0 }}>
                            {formatCurrency(stat.amount)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                          <Typography fontSize={12} color="#8a948d">
                            {stat.count}件
                          </Typography>
                          <Typography fontSize={12} color="#FCD34D">
                            支出全体の{stat.share}%
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {activeChartTab === 'count' && (
          <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Timeline sx={{ fontSize: 20, color: '#3B82F6', mr: 1 }} />
                  <Typography fontSize={17} fontWeight="bold" color="white">
                    件数推移
                  </Typography>
                </Box>
                <Typography fontSize={14} color="#8a948d">
                  最大30日
                </Typography>
              </Box>

              {dailyFinancialData.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                  <ShowChart sx={{ fontSize: 48, color: '#444', mb: 1 }} />
                  <Typography fontSize={16} color="#666">データがありません</Typography>
                </Box>
              ) : (
                <ResponsiveContainer width="100%" height={240}>
                  <LineChart data={dailyFinancialData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                    <XAxis dataKey="date" stroke="#888" fontSize={10} tick={{ fill: '#888' }} />
                    <YAxis stroke="#888" fontSize={10} tick={{ fill: '#888' }} />
                    <Tooltip
                      contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                      labelStyle={{ color: '#fff' }}
                      formatter={(value: number) => [`${value.toLocaleString()}件`, '配達件数']}
                    />
                    <Line type="monotone" dataKey="count" stroke="#3B82F6" strokeWidth={2} dot={{ fill: '#3B82F6', r: 4 }} />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>
        )}

        {/* プラットフォーム別売上 */}
        {activeChartTab === 'service' && serviceStats.length > 0 && (
          <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
            <CardContent sx={{ p: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PieChartIcon sx={{ fontSize: 20, color: '#3B82F6', mr: 1 }} />
                  <Typography fontSize={17} fontWeight="bold" color="white">
                    サービス別比較
                  </Typography>
                </Box>
                <Typography fontSize={14} color="#8a948d">
                  売上・時給
                </Typography>
              </Box>

              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={serviceStats.slice(0, 6)}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis dataKey="name" stroke="#888" fontSize={10} tick={{ fill: '#888' }} />
                  <YAxis stroke="#888" fontSize={10} tick={{ fill: '#888' }} tickFormatter={(value) => `¥${value}`} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: number, name: string) => [`¥${value.toLocaleString()}`, name === 'revenue' ? '売上' : '時給']}
                  />
                  <Bar dataKey="revenue" fill="#10B981" radius={[6, 6, 0, 0]} />
                  <Bar dataKey="hourlyRate" fill="#8B5CF6" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>

              <Divider sx={{ my: 2, bgcolor: '#26312b' }} />

              <ResponsiveContainer width="100%" height={300}>
                <RechartsPieChart>
                  <Pie
                    data={platformData}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    dataKey="value"
                    nameKey="name"
                    label={({ name, percent, x, y, value }: PieLabelRenderProps) => {
                      const percentage = (Number(percent || 0) * 100).toFixed(0);
                      const formattedValue = Number(value || 0).toLocaleString();
                      return (
                        <text
                          x={x}
                          y={y}
                          fill="#888"
                          textAnchor="middle"
                          dominantBaseline="central"
                          fontSize={10}
                        >
                          {`${name} ${percentage}% ¥${formattedValue}`}
                        </text>
                      );
                    }}
                  >
                    {platformData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#1a1a1a',
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: number, name: string) => [
                      `¥${value.toLocaleString()}`,
                      `${name} (${((value / totalRevenue) * 100).toFixed(1)}%)`
                    ]}
                  />
                  <Legend
                    wrapperStyle={{
                      color: '#fff',
                      fontSize: 12
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
                {serviceStats.slice(0, 6).map((stat, index) => (
                  <Box key={stat.name} sx={{ bgcolor: 'rgba(255,255,255,0.045)', borderRadius: '8px', p: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, mb: 0.75 }}>
                      <Typography fontSize={15} fontWeight={800} color="white">{index + 1}. {stat.name}</Typography>
                      <Typography fontSize={15} fontWeight={800} color="#10B981">{formatCurrency(stat.revenue)}</Typography>
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 0.75 }}>
                      <Typography fontSize={12} color="#c4b5fd">時給 {formatCurrency(stat.hourlyRate)}</Typography>
                      <Typography fontSize={12} color="#FCD34D">単価 {formatCurrency(stat.avgRevenue)}</Typography>
                      <Typography fontSize={12} color="#93C5FD">{stat.count}件</Typography>
                      <Typography fontSize={12} color="#6EE7B7">{formatMinutes(stat.minutes)}</Typography>
                    </Box>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        )}

        {/* 時給推移 */}
        {activeChartTab === 'hourly' && (
        <Card sx={{ bgcolor: '#121713', border: '1px solid #26312b', borderRadius: '12px', mb: 2 }}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Timeline sx={{ fontSize: 20, color: '#8B5CF6', mr: 1 }} />
                <Typography fontSize={17} fontWeight="bold" color="white">
                  時給推移
                </Typography>
              </Box>
              <Typography fontSize={14} color="#8a948d">
                稼働時間ベース
              </Typography>
            </Box>

            {hourlyRateData.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8 }}>
                <ShowChart sx={{ fontSize: 48, color: '#444', mb: 1 }} />
                <Typography fontSize={16} color="#666">
                  データがありません
                </Typography>
              </Box>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <LineChart data={hourlyRateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#888" 
                    fontSize={10}
                    tick={{ fill: '#888' }}
                  />
                  <YAxis 
                    stroke="#888" 
                    fontSize={10}
                    tick={{ fill: '#888' }}
                    tickFormatter={(value) => `¥${value}`}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a1a', 
                      border: '1px solid #333',
                      borderRadius: '8px'
                    }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value: number) => [`¥${value.toLocaleString()}`, '時給']}
                  />
                  <Line 
                    type="monotone" 
                    dataKey="rate" 
                    stroke="#8B5CF6" 
                    strokeWidth={2}
                    dot={{ fill: '#8B5CF6', r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        )}

      </Box>

      {/* 特定日選択ダイアログ */}
      <Dialog
        open={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '16px' } }}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={prevMonth} sx={{ color: '#888' }}>
              <ChevronLeft />
            </IconButton>
            <Typography fontSize={17} fontWeight="bold">
              {calendarYM.year}年{calendarYM.month + 1}月
            </Typography>
            <IconButton onClick={nextMonth} sx={{ color: '#888' }}>
              <ChevronRight />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
              <Box key={i} sx={{ textAlign: 'center', py: 0.5 }}>
                <Typography fontSize={13} color="#888">{day}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {Array.from({ length: firstDayOfWeek }, (_, i) => (
              <Box key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(calendarYM.year, calendarYM.month, day);
              const isSelected = calendarDate && isSameDay(date, calendarDate);
              
              return (
                <Box
                  key={day}
                  onClick={() => {
                    setCalendarDate(date);
                    setSelectedSpecificDate(date);
                    setSelectedPeriod('specific');
                    setShowDatePicker(false);
                  }}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: isSelected ? '#10B981' : 'transparent',
                    color: isSelected ? '#000' : '#fff',
                    fontWeight: isSelected ? 'bold' : 'normal',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: isSelected ? '#0d9668' : '#2a2a2a'
                    }
                  }}
                >
                  <Typography fontSize={16}>{day}</Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Dialog>

      {/* 期間選択ダイアログ */}
      <Dialog
        open={showDateRangePicker}
        onClose={() => setShowDateRangePicker(false)}
        PaperProps={{ sx: { bgcolor: '#1a1a1a', color: 'white', borderRadius: '16px' } }}
      >
        <Box sx={{ p: 2 }}>
          <Typography fontSize={17} fontWeight="bold" sx={{ mb: 2 }}>
            {selectingStart ? '開始日を選択' : '終了日を選択'}
          </Typography>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={rangePrevMonth} sx={{ color: '#888' }}>
              <ChevronLeft />
            </IconButton>
            <Typography fontSize={17} fontWeight="bold">
              {rangeCalendarYM.year}年{rangeCalendarYM.month + 1}月
            </Typography>
            <IconButton onClick={rangeNextMonth} sx={{ color: '#888' }}>
              <ChevronRight />
            </IconButton>
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {['日', '月', '火', '水', '木', '金', '土'].map((day, i) => (
              <Box key={i} sx={{ textAlign: 'center', py: 0.5 }}>
                <Typography fontSize={13} color="#888">{day}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {Array.from({ length: rangeFirstDayOfWeek }, (_, i) => (
              <Box key={`empty-${i}`} />
            ))}
            {Array.from({ length: rangeDaysInMonth }, (_, i) => {
              const day = i + 1;
              const date = new Date(rangeCalendarYM.year, rangeCalendarYM.month, day);
              const isStart = rangeStart && isSameDay(date, rangeStart);
              const isEnd = rangeEnd && isSameDay(date, rangeEnd);
              const inRange = isInRange(date);
              
              return (
                <Box
                  key={day}
                  onClick={() => {
                    if (selectingStart) {
                      setRangeStart(date);
                      setRangeEnd(null);
                      setSelectingStart(false);
                    } else {
                      setRangeEnd(date);
                      setSelectingStart(true);
                    }
                  }}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '8px',
                    bgcolor: (isStart || isEnd) ? '#10B981' : inRange ? '#2a4a3a' : 'transparent',
                    color: (isStart || isEnd) ? '#000' : '#fff',
                    fontWeight: (isStart || isEnd) ? 'bold' : 'normal',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: (isStart || isEnd) ? '#0d9668' : inRange ? '#1a3a2a' : '#2a2a2a'
                    }
                  }}
                >
                  <Typography fontSize={16}>{day}</Typography>
                </Box>
              );
            })}
          </Box>

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setRangeStart(null);
                setRangeEnd(null);
                setSelectingStart(true);
              }}
              sx={{
                borderColor: '#444',
                color: '#888',
                '&:hover': { borderColor: '#666', bgcolor: 'transparent' }
              }}
            >
              リセット
            </Button>
            <Button
              fullWidth
              variant="contained"
              disabled={!rangeStart || !rangeEnd}
              onClick={() => {
                if (rangeStart && rangeEnd) {
                  const start = rangeStart < rangeEnd ? rangeStart : rangeEnd;
                  const end = rangeStart < rangeEnd ? rangeEnd : rangeStart;
                  setSelectedStartDate(start);
                  setSelectedEndDate(end);
                  setSelectedPeriod('range');
                  setShowDateRangePicker(false);
                }
              }}
              sx={{
                bgcolor: rangeStart && rangeEnd ? '#10B981' : '#444',
                color: rangeStart && rangeEnd ? '#000' : '#666',
                fontWeight: 'bold',
                '&:hover': { bgcolor: rangeStart && rangeEnd ? '#0d9668' : '#444' },
                '&:disabled': { bgcolor: '#444', color: '#666' }
              }}
            >
              決定
            </Button>
          </Box>
        </Box>
      </Dialog>
    </Box>
  );
}

export default ChartsScreen;

